import React, { useState, useEffect } from "react";
import NavBar from "../../layout/navbar/navbar";
import AlertBanner from "../../layout/navbar/alert-banner";
import CardFinancing from "./card-financing";
import CardPerks from "./card-perks";
import CardAnnouncement from "./card-announcement";
import CardStatistics from "./card-statistics";
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  Button,
  Spinner,
} from "react-bootstrap";
import { useSupabaseApp } from "../../SupabaseApp";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { IoMdClose } from "react-icons/io";
import { BiAnalyse } from "react-icons/bi";
import "./dashboard copy.css";
import FeedDisplay from "./rss-feed";
import SociableKitWidget from "./sociablekit";

/**
 * DashboardCopy Component
 *
 * Main dashboard component that displays key business statistics, financing options, partner perks,
 * announcements, RSS feeds, and social media widgets. Aggregates data from multiple sources and
 * renders various card components to visualize metrics. Requires user authentication.
 *
 * The dashboardStatistics state contains 20+ metric categories including:
 * - Funding sources by stage (very early, early, growth, late, post-IPO)
 * - User, investor, company, and product counts
 * - Active/inactive company statistics
 * - Investor loss tracking
 * - Product discontinuation rates
 * - And more specialized metrics
 *
 * @returns {JSX.Element} Complete dashboard layout with navigation, statistics cards, feeds, and widgets
 */
const DashboardCopy = () => {
  // Get user context from Supabase authentication
  const { getUserDetails, currentUser, supabase } = useSupabaseApp();

  // Control visibility of alert messages on dashboard
  const [showAlert, setShowAlert] = useState(true);

  // Check if user is logged in before rendering dashboard
  if (!currentUser) {
    throw new Error(`Must be logged in`);
  }

  // Track whether initial data fetch has completed
  const [fetched, setFetched] = useState(false);

  // State to manage all dashboard statistics metrics
  // Contains arrays of stat objects with label and stats value for each metric category
  const [dashboardStatistics, setDashboardStatistics] = useState({
    veryEarlyStageFunding: [
      {
        label: "Financing sources",
        stats: 0,
      },
    ],
    earlyStageFunding: [
      {
        label: "Financing sources",
        stats: 0,
      },
    ],
    growthStageFunding: [
      {
        label: "Financing sources",
        stats: 0,
      },
    ],
    lateStageFunding: [
      {
        label: "Financing sources",
        stats: 0,
      },
    ],
    postIpoStageFunding: [
      {
        label: "Financing sources",
        stats: 0,
      },
    ],
    totalUsers: [
      {
        label: "Users",
        stats: 0,
      },
    ],
    totalInvestors: [
      {
        label: "Investors",
        stats: 0,
      },
    ],
    totalCompanies: [
      {
        label: "Companies",
        stats: 0,
      },
    ],
    totalActiveCompanies: [
      {
        label: "Companies",
        stats: 0,
      },
    ],
    totalInactiveCompanies: [
      {
        label: "Companies",
        stats: 0,
      },
    ],
    totalProducts: [
      {
        label: "Products",
        stats: 0,
      },
    ],
    totalPitchDecks: [
      {
        label: "Pitch decks",
        stats: 0,
      },
    ],
    pitchDecksShowcase: [
      {
        label: "Pitch decks",
        stats: 0,
      },
    ],
    activeCompanies: [
      {
        label: "Active companies",
        stats: 0,
      },
      {
        label: "Status unknown",
        stats: 0,
      },
    ],
    shutteredCompanies: [
      {
        label: "Inactive companies",
        stats: 0,
      },
      {
        label: "Reasons for shutdown",
        stats: 0,
      },
    ],
    investorsLosses: [
      {
        label: "Investors",
        stats: 0,
      },
      {
        label: "Failed investments",
        stats: 0,
      },
    ],
    discontinuedProducts: [
      {
        label: "Dead products",
        stats: 0,
      },
      {
        label: "Reasons for discontinuation",
        stats: 0,
      },
    ],
    productsShowcase: [
      {
        label: "Active products",
        stats: 0,
      },
      {
        label: "Status unknown",
        stats: 0,
      },
    ],
    postmortemAnalysis: [
      {
        label: "Inactive companies",
        stats: 0,
      },
      {
        label: "Reasons for shutdown",
        stats: 0,
      },
    ],
    portfolioAnalysis: [
      {
        label: "Portfolio companies",
        stats: 0,
      },
      {
        label: "Investors",
        stats: 0,
      },
    ],
    competitiveAnalysis: [
      {
        label: "Companies",
        stats: 0,
      },
    ],
    ideaValidation: [
      {
        label: "Entities",
        stats: 0,
      },
    ],
    date: "",
    usersDate: [],
  });

  // hook for date
  const [usersDate, setUsersDate] = useState("");

  // hook for alert component
  const [alert, setAlert] = useState(true);

  // hook for alert message
  const [message, setMessage] = useState(
    <div style={{ display: "flex", paddingTop: "10px" }}>
      <div style={{ width: "90%" }}>
        <p style={{ color: "#0f9d58" }}>
          <BiAnalyse className="alert-icon" /> Loading
        </p>
        <p style={{ marginLeft: "28px", fontSize: "14px" }}>
          Won't be long now. We're always doing our best to reduce waiting time.
        </p>
        <p style={{ marginLeft: "28px", fontSize: "14px" }}>
          If your waiting time is too long,{" "}
          <a
            href="https://www.roiquant.com/contact"
            target="_blank"
            rel="noopener noreferrer"
          >
            contact us
          </a>
          .
        </p>
      </div>
      <div style={{ width: "10%", textAlign: "right" }}>
        <Spinner animation="border" variant="secondary" />
      </div>
    </div>,
  );

  // function to close alert component
  function closeAlert() {
    setAlert(false);
    setMessage("");
  }

  // useEffect function to get dashboard stats
  useEffect(() => {
    async function fetchDashboardData() {
      try {
        let { data, error } = await supabase.functions.invoke(
          "getDashboardStatistics",
        );

        if (error) {
          throw error;
        }

        setDashboardStatistics(data);
        setFetched(true);

        let stats = data;

        if (stats.usersDate != []) {
          var monthArray = [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
          ];
          let dateString = new Date(
            stats.usersDate.map((doc) => doc.registerDate)[0],
          );
          var dateFormat =
            dateString.getDate() + " " + monthArray[dateString.getMonth()];

          setUsersDate(dateFormat);
        }

        closeAlert();
      } catch (err) {
        setMessage(
          <div style={{ paddingTop: "10px" }}>
            <p style={{ fontSize: "16px", color: "red" }}>
              <AiOutlineCloseCircle className="alert-icon" /> Bad internet
              issues or server connection errors
            </p>
            <p style={{ marginLeft: "28px", fontSize: "14px" }}>
              Sorry, there seems to be poor internet connection or interrupted
              connectivity issues or server connection errors. Should the issues
              persist,{" "}
              <a
                href="https://www.roiquant.com/contact"
                target="_blank"
                rel="noopener noreferrer"
              >
                contact us
              </a>
              .
            </p>
          </div>,
        );
      }
    }

    fetchDashboardData();
  }, []);

  // State to hold fetched user details
  const [userDetails, setUserDetails] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchUserDetails = async () => {
      try {
        setLoadingUser(true);
        const details = await getUserDetails();
        if (isMounted) setUserDetails(details);
      } catch (err) {
        console.error("Failed to fetch user details:", err);
      } finally {
        if (isMounted) setLoadingUser(false);
      }
    };

    fetchUserDetails();

    return () => {
      isMounted = false;
    };
  }, [getUserDetails]);

  function closeBanner() {
    setShowAlert(false);
  }

  const staticObjects = [
    <CardFinancing />,
    <CardPerks />,
    <CardAnnouncement date={dashboardStatistics.date} />,
  ];

  let cardObjects = [];

  if (fetched) {
    cardObjects = [
      <CardStatistics
        type="fundingSources"
        object={dashboardStatistics}
        date={dashboardStatistics.date}
      />,
      <CardStatistics
        type="otherStats"
        object={dashboardStatistics}
        date={dashboardStatistics.date}
      />,
      <CardStatistics
        type="numbersOverview"
        object={dashboardStatistics}
        date={dashboardStatistics.date}
        usersDate={usersDate}
      />,
    ];
  }

  return (
    <>
      <NavBar />

      {/* {showAlert ? <AlertBanner closeAlert={closeBanner} /> : ""} */}

      {alert ? (
        <Alert className="error-message floating-alert" variant="light">
          <div>
            <div style={{ textAlign: "right" }}>
              <Button className="pitchdeckmessageclose" onClick={closeAlert}>
                <IoMdClose />
              </Button>
            </div>
            {message}
          </div>
        </Alert>
      ) : (
        ""
      )}

      <div id="dashboard">
        <Container
          fluid
          className="dashboardContainer"
          style={{ paddingBottom: "200px" }}
        >
          <p className="dashboardHeader">
            {userDetails ? (
              <>
                Welcome, {userDetails.firstname} {userDetails.lastname}
              </>
            ) : (
              <>Loading your dashboard...</>
            )}
          </p>
          <Row xs={1} sm={1} lg={3}>
            {staticObjects.map((object, index) => (
              <Col key={index}>
                <Card>
                  <Card.Body className="dashboardCard">{object}</Card.Body>
                </Card>
              </Col>
            ))}

            {cardObjects.map((object, index) => (
              <Col key={index}>
                <Card>
                  <Card.Body className="dashboardCard">{object}</Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          <FeedDisplay
            setAlert={setAlert}
            setMessage={setMessage}
            closeAlert={closeAlert}
          />

          <Row>
            <Col>
              <p
                className="dashboardHeader"
                style={{ padding: "50px 0px 16px 0px", marginBottom: "0" }}
              >
                Thought leadership
              </p>

              <div className="dashboardWidgetDiv">
                <SociableKitWidget />
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default DashboardCopy;
